import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore, apiClient } from '../store/authStore';
import {
  MagnifyingGlass, Globe, Spinner, CheckCircle, WarningCircle, Info,
  ArrowRight, MagicWand,
} from '@phosphor-icons/react';

const C = {
  bg:'#0a0a0f', surface:'#13131f', deep:'#0d0d18', input:'#1a1a2e',
  border:'rgba(124,58,237,0.2)', borderHi:'rgba(124,58,237,0.4)',
  text:'#e8e8f0', muted:'#9090b0', hint:'#5a5a7a',
  violet:'#7c3aed', blue:'#3b82f6',
  grad:'linear-gradient(135deg, #7c3aed 0%, #3b82f6 100%)',
  green:'#10b981', red:'#f87171', amber:'#fbbf24',
};

const FUN = [
  'Crawling your pages…', 'Reading your meta tags…', 'Checking Core Web Vitals…',
  'Hunting for SEO issues…', 'Measuring content quality…', 'Scoring your site…',
  'Almost there — polishing the report…',
];

export default function OnboardingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading: authLoading } = useAuthStore();
  const [step, setStep] = useState(1);
  const [url, setUrl] = useState('');
  const [error, setError] = useState('');
  const [progress, setProgress] = useState(0);
  const [funIdx, setFunIdx] = useState(0);
  const [analysisId, setAnalysisId] = useState(null);
  const [report, setReport] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) navigate('/login');
  }, [authLoading, isAuthenticated, navigate]);

  useEffect(() => {
    if (step !== 2) return;
    const t = setInterval(() => setFunIdx(i => (i + 1) % FUN.length), 1800);
    return () => clearInterval(t);
  }, [step]);

  const normalize = (u) => {
    let v = u.trim();
    if (!/^https?:\/\//i.test(v)) v = 'https://' + v;
    return v;
  };

  const start = async () => {
    setError('');
    let site;
    try { site = normalize(url); new URL(site); }
    catch { setError('Please enter a valid website URL'); return; }
    setStep(2); setProgress(5);
    try {
      const project = await apiClient.post('/projects', { name: new URL(site).hostname, url: site });
      const analysis = await apiClient.post('/analyses', { project_id: project.project_id, url: site, keywords: [] });
      setAnalysisId(analysis.analysis_id);
      pollRef.current = setInterval(async () => {
        try {
          const s = await apiClient.get(`/analyses/${analysis.analysis_id}/status`);
          setProgress(Math.max(8, s.progress || 0));
          if (s.status === 'completed') {
            clearInterval(pollRef.current);
            const rep = await apiClient.get(`/reports/${analysis.analysis_id}`);
            setReport(rep); setProgress(100); setStep(3);
          } else if (s.status === 'failed') {
            clearInterval(pollRef.current);
            setError(s.error || 'Audit failed'); setStep(1);
          }
        } catch (e) { /* keep polling */ }
      }, 2000);
    } catch (e) {
      setError(e.message || 'Could not start the audit'); setStep(1);
    }
  };

  useEffect(() => () => pollRef.current && clearInterval(pollRef.current), []);

  const finish = async (path) => {
    try { await apiClient.post('/auth/onboarding-complete', {}); } catch (e) { /* non-blocking */ }
    navigate(path);
  };

  const scoreColor = (s) => s >= 80 ? C.green : s >= 50 ? C.amber : C.red;
  const sevIcon = (sev) => sev === 'critical'
    ? <WarningCircle weight="fill" style={{ width:16, height:16, color:C.red }} />
    : sev === 'warning' ? <WarningCircle weight="fill" style={{ width:16, height:16, color:C.amber }} />
    : <Info weight="fill" style={{ width:16, height:16, color:C.blue }} />;

  const top3 = report ? [...(report.issues || [])]
    .sort((a, b) => rank(a.severity) - rank(b.severity)).slice(0, 3) : [];

  return (
    <div style={{ minHeight:'100vh', background:C.bg, fontFamily:"'IBM Plex Sans', system-ui, sans-serif", color:C.text, display:'flex', flexDirection:'column' }}>
      {/* Steps header */}
      <div style={{ maxWidth:560, width:'100%', margin:'0 auto', padding:'40px 24px 0' }}>
        <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:36 }}>
          {[1, 2, 3].map(n => (
            <div key={n} style={{ flex:1, display:'flex', alignItems:'center', gap:8 }}>
              <div style={{ width:26, height:26, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:800,
                background: step >= n ? C.grad : C.input, color: step >= n ? '#fff' : C.hint, flexShrink:0 }}>
                {step > n ? <CheckCircle weight="fill" style={{ width:15, height:15 }} /> : n}
              </div>
              {n < 3 && <div style={{ flex:1, height:2, background: step > n ? C.violet : C.input, borderRadius:2 }} />}
            </div>
          ))}
        </div>
      </div>

      <div style={{ flex:1, display:'flex', alignItems:'flex-start', justifyContent:'center', padding:'0 24px 60px' }}>
        <div style={{ width:'100%', maxWidth:560 }}>

          {step === 1 && (
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, padding:'32px 28px' }}>
              <h1 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:24, fontWeight:900, margin:'0 0 8px', letterSpacing:'-0.02em' }}>Enter your website URL</h1>
              <p style={{ fontSize:14, color:C.muted, margin:'0 0 22px' }}>We'll run your first SEO audit and show you exactly what to fix.</p>
              <div style={{ position:'relative', marginBottom:14 }}>
                <Globe style={{ position:'absolute', left:13, top:'50%', transform:'translateY(-50%)', width:17, height:17, color:C.hint }} />
                <input value={url} onChange={e => setUrl(e.target.value)} onKeyDown={e => e.key === 'Enter' && start()}
                  placeholder="yourwebsite.com" autoFocus
                  style={{ width:'100%', height:48, paddingLeft:40, paddingRight:14, background:C.input, border:`1px solid ${C.border}`, borderRadius:10, fontSize:15, color:C.text, outline:'none', boxSizing:'border-box', fontFamily:'inherit' }} />
              </div>
              {error && <p style={{ fontSize:13, color:C.red, margin:'0 0 14px' }}>{error}</p>}
              <button onClick={start} style={{ width:'100%', height:48, background:C.grad, border:'none', borderRadius:10, fontSize:15, fontWeight:800, color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, fontFamily:'inherit', boxShadow:'0 2px 14px rgba(124,58,237,0.3)' }}>
                Next <ArrowRight weight="bold" style={{ width:16, height:16 }} />
              </button>
            </div>
          )}

          {step === 2 && (
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, padding:'40px 28px', textAlign:'center' }}>
              <Spinner className="animate-spin" style={{ width:34, height:34, color:C.violet, margin:'0 auto 18px' }} />
              <h1 style={{ fontFamily:"'Satoshi', sans-serif", fontSize:22, fontWeight:900, margin:'0 0 8px' }}>Launching your first audit…</h1>
              <p style={{ fontSize:14, color:C.violet, margin:'0 0 22px', minHeight:20 }}>{FUN[funIdx]}</p>
              <div style={{ height:8, background:C.input, borderRadius:6, overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${progress}%`, background:C.grad, borderRadius:6, transition:'width 0.5s ease' }} />
              </div>
              <p style={{ fontSize:12, color:C.hint, margin:'10px 0 0' }}>{progress}%</p>
            </div>
          )}

          {step === 3 && report && (
            <div style={{ background:C.surface, border:`1px solid ${C.border}`, borderRadius:14, padding:'32px 28px', textAlign:'center' }}>
              <p style={{ fontSize:12, fontWeight:700, color:C.hint, textTransform:'uppercase', letterSpacing:'0.08em', margin:'0 0 4px' }}>Your SEO Score is ready!</p>
              <div style={{ fontSize:64, fontWeight:900, fontFamily:"'JetBrains Mono', monospace", color:scoreColor(report.overall_score), lineHeight:1.1, margin:'4px 0' }}>{report.overall_score}</div>
              <p style={{ fontSize:13, color:C.muted, margin:'0 0 22px' }}>{new URL(report.url).hostname}</p>

              {top3.length > 0 && (
                <div style={{ textAlign:'left', display:'flex', flexDirection:'column', gap:8, marginBottom:24 }}>
                  <span style={{ fontSize:12, fontWeight:700, color:C.hint, textTransform:'uppercase', letterSpacing:'0.06em' }}>Top issues to fix</span>
                  {top3.map((i, k) => (
                    <div key={k} style={{ display:'flex', alignItems:'center', gap:10, background:C.deep, border:`1px solid ${C.border}`, borderRadius:9, padding:'11px 13px' }}>
                      {sevIcon(i.severity)}
                      <span style={{ fontSize:13.5, color:C.text }}>{i.title}</span>
                    </div>
                  ))}
                </div>
              )}

              <button onClick={() => finish(`/report/${analysisId}`)} style={{ width:'100%', height:48, background:C.grad, border:'none', borderRadius:10, fontSize:15, fontWeight:800, color:'#fff', cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8, fontFamily:'inherit', boxShadow:'0 2px 14px rgba(124,58,237,0.3)' }}>
                <MagicWand weight="fill" style={{ width:16, height:16 }} /> Fix automatically
              </button>
              <button onClick={() => finish('/dashboard')} style={{ marginTop:10, background:'transparent', border:'none', color:C.muted, fontSize:13, cursor:'pointer', fontFamily:'inherit' }}>Skip to dashboard</button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

function rank(sev) { return sev === 'critical' ? 0 : sev === 'warning' ? 1 : 2; }
